import { useContext } from 'react'
import { toast } from 'react-toastify'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileDownload, faRepeat, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import {
  Button, FileInput, FileUploadZone, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAttachment, { useAttachmentForm } from '@hooks/useAttachment'
import useUppyAttachmentUpload from '@hooks/useUppyAttachmentUpload'
import useAssetLibrary from '@hooks/useAssetLibrary'

import { confirmDelete } from '@functions/entity'

const MODAL_KEY = 'CreateOrEditAttachmentModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateAttachment: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const createAttachment = (closeModal: ModalCallbacks['closeModal']) => {
  toast('Attachment Created Successfully')
  closeModal()
}

const defaultState = {
  showFileUpload: false,
}

const CreateOrEditAttachmentModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateAttachment } = callbacks

  const [state, setState] = useSetState(defaultState)
  const { showFileUpload } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    attachment: initAttachment,
    hideDelete,
    hideReplace,
    hideUpload,
  } = modalPayload

  const {
    attachment,
    callbacks: {
      deleteAttachment: deleteFn,
      downloadAttachment,
      updateAttachment: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useAttachment(initAttachment)

  const {
    entityState,
    entityState: {
      attachable_id,
      attachable_type,
      data,
      file_file_name,
      id,
    },
    handlers,
    saveEnabled,
    setEntityState,
  } = useAttachmentForm(attachment)

  const { description, title } = data

  const uppy = useUppyAttachmentUpload(
    { attachmentParams: initAttachment, successCallback: () => createAttachment(closeModal) },
    // @ts-expect-error TS2345
    [...Object.values(initAttachment)],
  )

  const updateAttachmentPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.attachment,
    toastText: 'Attachment Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Attachment',
    },
  }

  const saveAttachmentFn = id ? () => updateAttachment(updateAttachmentPayload) : closeModal

  const { editable } = useAssetLibrary({ id: attachable_id })

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="Attachment" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Title">
            <input disabled={!editable} name="data.title" type="text" value={title} {...handlers} />
          </Form.Field>

          {!id && !hideUpload && (
            <Form.Field label="* Upload File" marginTop="large">
              <FileUploadZone uppy={uppy} />
            </Form.Field>
          )}

          {id && !showFileUpload && (
            <Form.Field label="File Name" marginTop="large">
              <input disabled value={file_file_name} type="text" />
            </Form.Field>
          )}

          {id && !showFileUpload && attachable_type === 'AssetLibrary' && (
            <Form.Field label="Description" marginTop="large">
              <textarea
                disabled={!editable}
                name="data.description"
                style={{ height: 120, resize: 'vertical' }}
                // @ts-expect-error TS2322
                type="text"
                value={description}
                {...handlers}
              />
            </Form.Field>
          )}

          {id && showFileUpload && (
            <Form.Field label="* File" marginTop="large">
              <FileInput
                input={{
                  // @ts-expect-error TS7006
                  onChange: (fileData, fileObject) => (
                    setEntityState({ file: fileData, file_file_name: fileObject.name })
                  ),
                }}
              />
            </Form.Field>
          )}
        </Form>

        {id && (
          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faFileDownload} />}
              // @ts-expect-error TS2322
              onClick={e => downloadAttachment(e)}
              size="medium"
            >
              Download Attachment
            </Button>

            {!hideDelete && editable && (
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error
                marginTop="medium"
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Attachment
              </Button>
            )}

            {!showFileUpload && !hideReplace && editable && (
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faRepeat} />}
                // @ts-expect-error
                marginTop="medium"
                onClick={() => setState({ showFileUpload: true })}
                size="medium"
              >
                Replace File
              </Button>
            )}
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        {editable && (
          <Button
            buttonStyle="primaryCreate"
            disabled={!saveEnabled}
            loading={creating || updating}
            onClick={() => saveAttachmentFn()}
            size="large"
          >
            {id ? 'Update Attachment' : 'Close'}
          </Button>
        )}

        {!editable && (
          <Button
            buttonStyle="secondaryUtility"
            onClick={() => closeModal()}
            size="large"
          >
            Close Modal
          </Button>
        )}
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAttachmentModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
